import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaSearch,
  FaFileExcel,
  FaPrint,
  FaFilter,
  FaTimes,
  FaPaperclip,
  FaEnvelope,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const GSTReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const primaryButtonClass = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white`;
  const secondaryButtonClass = "bg-gray-200 hover:bg-gray-300 text-gray-800";

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = {
    "x-finyearid": finYearId,
    "x-finyear": finYear,
    "x-branchid": storedBranchId,
    "x-branch": branch,
    "x-userid": userId,
  };

  const today = new Date().toISOString().split("T")[0];

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  const [branchId, setBranchId] = useState(storedBranchId || "8");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [taxType, setTaxType] = useState("RD");

  const [branchList, setBranchList] = useState([]);
  const [data, setData] = useState([]);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setLoading(true);

    axios
      .get("https://nissi-india.com/api/branchDropdown_gstRpt", {
        params: {
          branchType: branch || "HO",
          branchId: storedBranchId || 8,
        },
        headers: commonHeaders,
      })
      .then((res) => {
        if (res.data?.data) {
          setBranchList(res.data.data || []);
        }
      })
      .catch((err) => {
        console.error("Branch fetch failed", err);
        toast.error("Failed to load branch list");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleSearch = () => {
    if (!fromDate || !toDate || !taxType) {
      toast.warning("Please select From Date, To Date and GST Type");
      return;
    }

    setLoading(true);

    const params = {
      branchId,
      fromdate: fromDate,
      todate: toDate,
      taxType: taxType.toLowerCase(),
    };

    axios
      .get("https://nissi-india.com/api/gstReportForListing", {
        params,
        headers: commonHeaders,
      })
      .then((res) => {
        const rows = res.data?.data || [];
        setData(rows);
        setCurrentPage(1);
        toast.success(`Loaded ${rows.length} records`);
      })
      .catch((err) => {
        console.error("GST report fetch failed", err);
        toast.error("Failed to fetch GST report");
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleStatusChange = (invoiceId, status) => {
    axios
      .post(
        "https://nissi-india.com/api/updateInvoiceStatus_gstRpt",
        {
          invoiceId,
          status,
        },
        {
          headers: commonHeaders,
        }
      )
      .then(() => toast.success("Invoice status updated"))
      .catch((err) => {
        console.error("Status update failed", err);
        toast.error("Failed to update status");
      });
  };

  const handleViewInvoice = (invoiceNo) => {
    axios
      .get("https://nissi-india.com/api/grnInvoice_documentsRpt", {
        params: { invoiceno: invoiceNo },
        headers: commonHeaders,
      })
      .then((res) => {
        const docs = res.data?.data || [];
        if (docs.length > 0) {
          docs.forEach((doc) => {
            if (doc.url) {
              window.open(doc.url, "_blank");
            }
          });
        } else {
          toast.warning("No invoice document found");
        }
      })
      .catch((err) => {
        console.error("Invoice fetch failed", err);
        toast.error("Failed to fetch invoice copy");
      });
  };

  const handleExport = () => {
    if (!fromDate || !toDate || !taxType) {
      toast.warning("Please select From Date, To Date and GST Type");
      return;
    }

    const params = {
      fromDate,
      toDate,
      branchId,
      taxType: taxType.toLowerCase(),
    };

    axios
      .get("https://nissi-india.com/api/gstReportExcel", {
        params,
        headers: commonHeaders,
        responseType: "blob",
      })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `GST_Report_${fromDate}_to_${toDate}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Excel file downloaded successfully");
      })
      .catch((err) => {
        console.error("Excel export failed", err);
        toast.error("Failed to download Excel");
      });
  };

  const handlePrint = () => {
    const params = {
      fromDate,
      toDate,
      branchId,
      taxType: taxType.toLowerCase(),
    };

    const query = new URLSearchParams(params).toString();
    window.open(`/inventory/GSTReport_AllPrint?${query}`, "_blank");
  };

  const clearFilters = () => {
    setFromDate(today);
    setToDate(today);
    setTaxType("RD");
    setBranchId(storedBranchId || "8");
    setData([]);
    setCurrentPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d)) return value;
    return d.toLocaleDateString("en-GB");
  };

  const getStatusColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-800";

    const statusLower = String(status).toLowerCase();
    if (statusLower.includes("approve") || statusLower.includes("complete")) {
      return "bg-green-100 text-green-800";
    }
    if (statusLower.includes("pending") || statusLower.includes("waiting")) {
      return "bg-yellow-100 text-yellow-800";
    }
    if (statusLower.includes("reject") || statusLower.includes("cancel")) {
      return "bg-red-100 text-red-800";
    }
    return "bg-blue-100 text-blue-800";
  };

  // totals
  const totals = useMemo(() => {
    return data.reduce(
      (acc, row) => {
        acc.accAmt += Number(row.AccAmtNew || 0);
        acc.sgst += Number(row.SGSTAmountNew || 0);
        acc.cgst += Number(row.CGSTAmountNew || 0);
        acc.igst += Number(row.IGSTAmountNew || 0);
        acc.total += Number(row.TotalAmountNew || 0);
        return acc;
      },
      {
        accAmt: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
        total: 0,
      }
    );
  }, [data]);

  const totalPages = Math.ceil(data.length / rowsPerPage);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return data.slice(startIndex, startIndex + rowsPerPage);
  }, [data, currentPage, rowsPerPage]);

  const startRecord = data.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, data.length);

  return (
    <LayoutWrapper>
      <HeaderBar title="GST REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-xl shadow-sm border ${
          isDark
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">GST Report</h1>
            <p className="text-sm text-gray-500 mt-1">
              Track and manage GST compliance and invoices
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? primaryButtonClass : secondaryButtonClass
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={handleExport}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${secondaryButtonClass}`}
              disabled={data.length === 0}
            >
              <FaFileExcel className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-lg p-5 mb-6 ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Search Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" /> Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium mb-2">
                  From Date *
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  To Date *
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Branch *
                </label>
                <select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                >
                  {branchList.map((b) => (
                    <option key={b.fld_Branchid} value={b.fld_Branchid}>
                      {b.fld_BranchName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  GST Type *
                </label>
                <div className="flex flex-col gap-2 mt-1">
                  {["RD", "URD", "NoTax"].map((type) => (
                    <label
                      key={type}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="gsttype"
                        value={type}
                        checked={taxType === type}
                        onChange={(e) => setTaxType(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200">
              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 ${primaryButtonClass} px-4 py-2 rounded transition-colors`}
              >
                <FaSearch className="w-4 h-4" />
                Search
              </button>

              <button
                onClick={handlePrint}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                <FaPrint className="w-4 h-4" />
                Print Report
              </button>
            </div>
          </div>
        )}

        <div>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-3">
            <h2 className="text-lg font-semibold">
              Results {data.length > 0 && `(${data.length} records)`}
            </h2>

            {data.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="text-sm text-gray-500">
                  Total Amount:{" "}
                  <span className="font-semibold">
                    {formatCurrency(totals.total)}
                  </span>
                </span>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500">Rows:</label>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border rounded bg-white text-sm text-gray-800"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : data.length > 0 ? (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        GRN No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Supplier
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        GST No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        SGST
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CGST
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IGST
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((row, idx) => {
                      const serialNo = (currentPage - 1) * rowsPerPage + idx + 1;

                      return (
                        <tr
                          key={row.fld_billid || serialNo}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {serialNo}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            {row.fld_GRNno || "-"}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {row.fld_Suppliername || "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.fld_TIN || "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            {row.fld_invno || "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(row.BillDate)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(row.AccAmtNew)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(row.SGSTAmountNew)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(row.CGSTAmountNew)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(row.IGSTAmountNew)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(row.TotalAmountNew)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                                row.fld_status
                              )}`}
                            >
                              {row.fld_status || "Pending"}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewInvoice(row.fld_invno)}
                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                title="View Invoice"
                              >
                                <FaPaperclip className="w-4 h-4" />
                              </button>

                              <button
                                onClick={() =>
                                  toast.info(`Send mail for ${row.fld_invno}`)
                                }
                                className="text-green-600 hover:text-green-800 transition-colors"
                                title="Send Mail"
                              >
                                <FaEnvelope className="w-4 h-4" />
                              </button>

                              {/*
                              <button
                                onClick={() => handleStatusChange(row.fld_billid, "Approved")}
                                className="text-purple-600 hover:text-purple-800 transition-colors"
                                title="Approve"
                              >
                                <FaCheckCircle className="w-4 h-4" />
                              </button>
                              */}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot className="bg-gray-100">
                    <tr>
                      <td
                        colSpan="6"
                        className="px-4 py-3 text-right text-sm font-semibold text-gray-900"
                      >
                        Totals
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(totals.accAmt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(totals.sgst)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(totals.cgst)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(totals.igst)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(totals.total)}
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4">
                <div className="text-sm text-gray-500">
                  Showing {startRecord} to {endRecord} of {data.length} entries
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded border flex items-center gap-2 ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <FaChevronLeft className="w-3 h-3" />
                    Prev
                  </button>

                  <span className="px-3 py-2 text-sm font-medium">
                    Page {currentPage} of {totalPages || 1}
                  </span>

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages || 1))
                    }
                    disabled={currentPage === totalPages || totalPages === 0}
                    className={`px-3 py-2 rounded border flex items-center gap-2 ${
                      currentPage === totalPages || totalPages === 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    Next
                    <FaChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <FaCheckCircle className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No GST records
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GSTReport;